import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../../firebase"; 
// Import updateDoc for editing
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

function CategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- STATE FOR EDIT MODAL ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [newImageBase64, setNewImageBase64] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Category grocery'));
        const categoriesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesList);
      } catch (err) {
        console.error("Error fetching categories: ", err);
        setError('Failed to load categories.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // --- DELETE FUNCTION (Unchanged) ---
  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the category "${name}"?`)) {
      try {
        await deleteDoc(doc(db, 'Category grocery', id));
        setCategories(prev => prev.filter(category => category.id !== id));
        alert('Category deleted successfully!');
      } catch (err) {
        console.error("Error deleting category: ", err);
        alert('Failed to delete category.');
      }
    }
  };

  // --- EDIT FUNCTIONS ---
  
  // Opens the modal with the selected category's data
  const handleEdit = (category) => {
    setCurrentCategory(category);
    setNewImageBase64(''); // Reset new image state
    setIsModalOpen(true);
  };

  // Handles input changes within the modal
  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory(prev => ({ ...prev, [name]: value }));
  };

  // Handles new image selection in the modal
  const handleModalImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 750 * 1024) {
      alert('File is too large. Please select an image under 750KB.');
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setNewImageBase64(reader.result);
    reader.onerror = () => alert("Failed to read image file.");
  };
  
  // Submits the updated category data to Firestore
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentCategory) return;

    try {
      const categoryRef = doc(db, 'Category grocery', currentCategory.id);
      const updatedData = { ...currentCategory };

      // If a new image was selected, add its Base64 string to the update
      if (newImageBase64) {
        updatedData.imageUrl = newImageBase64;
      }
      
      await updateDoc(categoryRef, updatedData);

      // Update state locally to show changes instantly
      setCategories(prev => prev.map(cat => cat.id === currentCategory.id ? updatedData : cat));
      
      setIsModalOpen(false);
      alert('Category updated successfully!');
    } catch (err) {
      console.error("Error updating category: ", err);
      alert('Failed to update category.');
    }
  };

  if (loading) return <div style={styles.centered}>Loading...</div>;
  if (error) return <div style={{ ...styles.centered, color: 'red' }}>{error}</div>;

  return (
    <div style={styles.container}>
      {/* --- CATEGORY LIST TABLE --- */}
      <div style={styles.header}>
        <h2>Categories</h2>
        <button style={styles.addButton} onClick={() => navigate('/categories/add')}>
          <FaPlus style={{ marginRight: '8px' }} />
          Add Category
        </button>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Image</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Slug</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.length > 0 ? (
            categories.map(category => (
              <tr key={category.id}>
                <td style={styles.td}><img src={category.imageUrl} alt={category.name} style={styles.image} /></td>
                <td style={styles.td}>{category.name}</td>
                <td style={styles.td}>{category.slug}</td>
                <td style={styles.td}>
                  {/* Pass the entire category object to handleEdit */}
                  <button style={{...styles.actionButton, ...styles.editButton}} onClick={() => handleEdit(category)}>
                    <FaEdit />
                  </button>
                  <button style={{...styles.actionButton, ...styles.deleteButton}} onClick={() => handleDelete(category.id, category.name)}>
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="4" style={styles.centered}>No categories found.</td></tr>
          )}
        </tbody>
      </table>

      {/* --- EDIT CATEGORY MODAL --- */}
      {isModalOpen && currentCategory && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2>Edit Category</h2>
            <form onSubmit={handleUpdate}>
              <div style={styles.formGroup}>
                <label>Category Name</label>
                <input type="text" name="name" value={currentCategory.name} onChange={handleModalChange} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label>Category Slug</label>
                <input type="text" name="slug" value={currentCategory.slug} onChange={handleModalChange} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label>Image</label>
                <img src={newImageBase64 || currentCategory.imageUrl} alt="Current" style={{width: 100, height: 100, objectFit: 'cover', borderRadius: '4px'}}/>
                <input type="file" accept="image/*" onChange={handleModalImageChange} style={{marginTop: '10px'}}/>
                <small>Select a new file to change the image.</small>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.saveButton}>Save Changes</button>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- STYLES (with additions for Modal) ---
const styles = {
  container: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  addButton: { display: 'flex', alignItems: 'center', padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  th: { backgroundColor: '#f8f9fa', padding: '12px', border: '1px solid #ddd', textAlign: 'left' },
  td: { padding: '12px', border: '1px solid #ddd', verticalAlign: 'middle' },
  image: { width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' },
  actionButton: { border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', color: 'white', marginRight: '5px' },
  editButton: { backgroundColor: '#28a745' },
  deleteButton: { backgroundColor: '#dc3545' },
  centered: { textAlign: 'center', padding: '20px', fontSize: '18px' },
  // Modal Styles
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: 'white', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' },
  formGroup: { marginBottom: '1rem' },
  input: { width: '100%', padding: '10px', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid #ccc' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' },
  saveButton: { backgroundColor: '#28a745', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
};

export default CategoriesPage;